import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
type QuoteModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
export function QuoteModal({
  isOpen,
  onClose
}: QuoteModalProps) {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Reset after showing success
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      onClose();
    }, 2000);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-243">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8" onClick={e => e.stopPropagation()} data-id="element-244">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close modal" data-id="element-245">
          <X className="w-5 h-5" data-id="element-246" />
        </button>

        {/* Header */}
        <div className="mb-6" data-id="element-247">
          <h2 id="modal-title" className="text-2xl font-bold text-gray-900" data-id="element-248">
            {t.quoteModal.title}
          </h2>
          <p className="text-gray-600 mt-1" data-id="element-249">{t.quoteModal.subtitle}</p>
        </div>

        {isSubmitted ? <div className="text-center py-8" data-id="element-250">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-251">
              <Send className="w-8 h-8 text-emerald-600" data-id="element-252" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-253">
              {t.quoteModal.success.title}
            </h3>
            <p className="text-gray-600" data-id="element-254">{t.quoteModal.success.message}</p>
          </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-255">
            <div data-id="element-256">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-257">
                {t.quoteModal.fields.name.label} *
              </label>
              <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors" placeholder={t.quoteModal.fields.name.placeholder} data-id="element-258" />
            </div>

            <div data-id="element-259">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-260">
                {t.quoteModal.fields.email.label} *
              </label>
              <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors" placeholder={t.quoteModal.fields.email.placeholder} data-id="element-261" />
            </div>

            <div data-id="element-262">
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-263">
                {t.quoteModal.fields.company.label}
              </label>
              <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors" placeholder={t.quoteModal.fields.company.placeholder} data-id="element-264" />
            </div>

            <div data-id="element-265">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-266">
                {t.quoteModal.fields.message.label} *
              </label>
              <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors resize-none" placeholder={t.quoteModal.fields.message.placeholder} data-id="element-267" />
            </div>

            <button type="submit" disabled={isSubmitting} className="w-full py-3 px-6 bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-268">
              {isSubmitting ? <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-269" />
                  {t.quoteModal.submitting}
                </> : <>
                  <Send className="w-5 h-5" data-id="element-270" />
                  {t.quoteModal.submit}
                </>}
            </button>
          </form>}
      </div>
    </div>;
}